import React, { useState, useCallback, useMemo } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Modal, Spin } from 'antd';

import { Formik, Form, Field } from 'formik';
import type { FormikProps } from 'formik';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ListRadioButtonFormField } from 'v2/components/Shared/FormFields/ListRadioButton';
import { buyerCSAT } from 'v2/helpers/hotjar';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

import { RefundBuyerRequestSchema } from './validations';

export interface IBuyerRefundReason {
  motive: string;
  detail: string;
  invoiceId: string;
}

interface IProps {
  open: boolean;
  invoiceId: string | number;
  onClose: () => void;
  onFinish: (refundReason: IBuyerRefundReason) => void;
}

export default function BuyerRefundRequestModal({ open, invoiceId, onClose, onFinish }: IProps) {
  const { message } = App.useApp();
  const [refundReason, setRefundReason] = useState<IBuyerRefundReason>();

  const [onSubmit] = useCallbackObservable(
    (values: IBuyerRefundReason, { setSubmitting, resetForm }: FormikProps<IBuyerRefundReason>) => {
      setSubmitting(true);

      return refundRequestFactory.sendBuyerRefundRequest(values).pipe(
        tap(response => {
          const refundReason: IBuyerRefundReason = {
            ...values,
            ...{
              success: response.success,
              motive: response.request_motive_id,
              detail: response.description,
              invoiceId: response.invoice_id
            }
          };
          setRefundReason(refundReason);
          setSubmitting(false);
          resetForm();
          message.success('Solicitação realizada com sucesso!');
          onComplete();
          buyerCSAT();
        }),
        catchError(err => {
          setSubmitting(false);
          message.error(err instanceof Error ? err.message : 'Não foi possível solicitar o reembolso.');
          return of(true);
        })
      );
    },
    [message]
  );

  const onComplete = useCallback(() => {
    onFinish(refundReason);
    setRefundReason(null);
  }, [onFinish, refundReason]);

  const onHandleClose = useCallback(
    (resetForm: () => void) => {
      setRefundReason(null);
      onClose();
      resetForm();
    },
    [onClose]
  );

  const [motives] = useObservable(() => refundRequestFactory.getRefundMotive(), []);
  const [loading] = useObservable(() => refundRequestFactory.loading(), []);

  const motivesOptions = useMemo(
    () =>
      (motives || []).map(item => ({
        label: item.description,
        value: item.id
      })),
    [motives]
  );

  return (
    <Formik
      validationSchema={RefundBuyerRequestSchema}
      initialValues={{ motive: '', detail: '', invoiceId }}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, submitForm, isValid, dirty, resetForm }) => {
        const shouldDisableConfirmBtn = !isValid || !dirty || isSubmitting;

        return (
          <Form>
            <Modal
              open={open}
              onCancel={() => onHandleClose(resetForm)}
              onOk={submitForm}
              width={520}
              title='Motivo do reembolso'
              closable
              cancelText='Cancelar'
              cancelButtonProps={{ type: 'text' }}
              okText={isSubmitting ? 'Solicitando...' : 'Solicitar'}
              confirmLoading={isSubmitting}
              okButtonProps={{ htmlType: 'submit', disabled: shouldDisableConfirmBtn }}
            >
              {loading || isSubmitting ? (
                <div className='flex justify-center'>
                  <Spin />
                </div>
              ) : (
                <Field id='motive' name='motive' options={motivesOptions} component={ListRadioButtonFormField} />
              )}
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
}
